import React, { useMemo, useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useDeleteLabRow, useLabEvents } from '../../hooks/lab-hooks';
import {
  LabEvent,
  LabEventState,
  labEventBlockers,
  labEventModeLabel,
  labEventStateLabel,
  labEventStateSentiment,
  labSizeLabel,
  LAB_EVENT_STATES,
  LAB_TABLES,
} from '../../support/labService';
import LabEventEditorModal from './LabEventEditorModal';
import {
  LabCard,
  LabDate,
  LabOptionSelect,
  LabPanelState,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';

export type LabEventsPanelProps = {
  /** Scheduling and cancelling. The API refuses either way; this decides the buttons. */
  canEdit?: boolean;
  /** Narrow to one published lab. */
  configurationId?: number;
  /** Highlighted row, for a page whose other tabs are scoped by event. */
  selectedId?: number | null;
  onSelect?: (event: LabEvent) => void;
};

/**
 * Every occasion a lab is being delivered on.
 *
 * Cancelled and finished events are hidden by default. They are kept - an event is the record of
 * who was given what - but a list that shows every workshop since the spring is a list nobody
 * reads, and the thing somebody comes here to find is the one happening on Tuesday.
 */
const LabEventsPanel: React.FC<LabEventsPanelProps> = ({
  canEdit = false,
  configurationId,
  selectedId,
  onSelect,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; event: LabEvent | null }>({
    open: false,
    event: null,
  });
  const [stateFilter, setStateFilter] = useState<LabEventState | ''>('');
  const [showFinished, setShowFinished] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      ...(configurationId ? { configuration: configurationId } : {}),
      ...(stateFilter ? { state: stateFilter } : {}),
    }),
    [configurationId, stateFilter],
  );

  const { data: events, isLoading, error } = useLabEvents(params);
  const deleteEvent = useDeleteLabRow(LAB_TABLES.event);

  const rows = useMemo(() => {
    const all = events || [];

    if (showFinished || stateFilter) return all;

    return all.filter((event) => !['complete', 'cancelled'].includes(event.state));
  }, [events, showFinished, stateFilter]);

  const hiddenCount = (events || []).length - rows.length;

  const remove = async (event: LabEvent) => {
    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Delete ${event.name}? Its pools and the record of who was assigned a pod go with it. Cancelling it instead keeps both.`,
      )
    ) {
      return;
    }

    setRemoveError(null);

    try {
      await deleteEvent.mutateAsync(event.id);
    } catch (exc) {
      setRemoveError(labErrorMessage(exc, 'Could not delete the event.'));
    }
  };

  return (
    <>
      <LabCard
        title={`Events (${rows.length})`}
        subtitle="Which published lab, when, and for whom. The pod comes up from its dCloud template; the scenario is layered on once it is active."
        actions={
          <>
            <div className="min-w-[180px]">
              <LabOptionSelect
                label="State"
                value={stateFilter as LabEventState}
                options={LAB_EVENT_STATES}
                allowEmpty
                emptyLabel="Any state"
                onChange={(state) => setStateFilter(state || '')}
              />
            </div>
            {canEdit && (
              <StbButton
                role="add"
                size="small"
                onClick={async () => setEditor({ open: true, event: null })}
              >
                Schedule an event
              </StbButton>
            )}
          </>
        }
      >
        {removeError && <p className="m-0 mb-3 text-sm text-amber-800">{removeError}</p>}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!rows.length}
          emptyMessage={
            hiddenCount > 0
              ? `Nothing upcoming. ${hiddenCount} finished or cancelled event${
                  hiddenCount === 1 ? '' : 's'
                } hidden.`
              : 'No events yet. A published lab with no event is a lab nobody has been given.'
          }
          loadingLabel="Loading events..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Event</th>
                  <th className="px-3 py-2 font-semibold">Lab</th>
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Pods</th>
                  <th className="px-3 py-2 font-semibold">Owner</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((event) => {
                  const blockers = labEventBlockers(event);
                  const selected = selectedId === event.id;
                  const pools = event.pools || [];
                  // The backend's own verdict where it gave one - `labEventBlockers` explains it
                  // rather than replaces it, since the UI cannot see everything the model can.
                  const ready = event.is_ready ?? !blockers.length;

                  const stocked = pools.reduce(
                    (total, pool) => total + (pool.snapshot?.members_usable || 0),
                    0,
                  );
                  const live = pools.reduce(
                    (total, pool) => total + (pool.snapshot?.assignments_live || 0),
                    0,
                  );

                  return (
                    <tr
                      key={event.id}
                      className={`border-b border-gray-100 align-top ${selected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{event.name}</div>
                        <div className="text-xs text-gray-500">{event.slug}</div>

                        <div className="mt-1 flex flex-wrap gap-1">
                          <LabStatusTag
                            label={labEventStateLabel(event.state)}
                            sentiment={labEventStateSentiment(event.state)}
                          />
                          <LabStatusTag label={labEventModeLabel(event.mode)} />
                          {ready && <LabStatusTag label="Ready" sentiment="success" />}
                          {!event.auto_start_run && (
                            <LabStatusTag label="Runs started by hand" sentiment="warning" />
                          )}
                        </div>

                        {blockers.length > 0 && (
                          <ul className="mb-0 mt-1 list-disc pl-4 text-xs text-amber-800">
                            {blockers.map((blocker) => (
                              <li key={blocker}>{blocker}</li>
                            ))}
                          </ul>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <div>{event.configuration_name || `configuration ${event.configuration}`}</div>
                        <div className="text-xs text-gray-500">
                          {[event.scenario_name, event.data_center_name, labSizeLabel(event.size)]
                            .filter(Boolean)
                            .join(' • ')}
                        </div>
                      </td>

                      <td className="px-3 py-2 text-xs">
                        <div>
                          <LabDate value={event.starts_at} fallback="No start set" />
                        </div>
                        <div className="text-gray-500">
                          <LabDate value={event.ends_at} fallback="no end set" />
                        </div>
                      </td>

                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        {event.mode === 'single' ? (
                          <>
                            <div className="font-mono">{event.session_key || 'no session'}</div>
                            <div className="text-gray-500">{event.session_state || '-'}</div>
                          </>
                        ) : (
                          <>
                            <div>
                              {`${stocked} stocked${
                                event.seat_count ? ` of ${event.seat_count} expected` : ''
                              }`}
                            </div>
                            <div className="text-gray-500">
                              {`${live} in use • ${pools.length} pool${
                                pools.length === 1 ? '' : 's'
                              }`}
                            </div>
                          </>
                        )}
                      </td>

                      <td className="px-3 py-2 text-xs">
                        <div>{event.owner_email || '-'}</div>
                        {event.contact_email && event.contact_email !== event.owner_email && (
                          <div className="text-gray-500">{event.contact_email}</div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {onSelect && !selected && (
                            <StbButton
                              role="default"
                              size="xsmall"
                              onClick={async () => onSelect(event)}
                            >
                              Select
                            </StbButton>
                          )}
                          {canEdit && (
                            <>
                              <StbButton
                                role="update"
                                size="xsmall"
                                onClick={async () => setEditor({ open: true, event })}
                              >
                                Edit
                              </StbButton>
                              <StbButton
                                role="delete"
                                size="xsmall"
                                loading={deleteEvent.isPending}
                                onClick={async () => remove(event)}
                              >
                                Delete
                              </StbButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>

        {!stateFilter && (hiddenCount > 0 || showFinished) && (
          <button
            type="button"
            className="mt-3 text-xs text-blue-700 underline"
            onClick={() => setShowFinished((current) => !current)}
          >
            {showFinished
              ? 'Hide finished and cancelled'
              : `Show ${hiddenCount} finished or cancelled`}
          </button>
        )}
      </LabCard>

      <LabEventEditorModal
        open={editor.open}
        event={editor.event}
        defaultConfigurationId={configurationId}
        onClose={() => setEditor({ open: false, event: null })}
        onSaved={(saved) => onSelect?.(saved)}
      />
    </>
  );
};

export default LabEventsPanel;
